import emptyStateImage from "$assets/images/empty-state.svg?url";
import { Button } from "$components/button/button";
import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { Box, styled, Typography } from "@material-ui/core";
import _Pagination from "@material-ui/lab/Pagination";
import { format } from "date-fns";
import React, { useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import {
  GroupedHistoricEvents,
  HistoricEventsForDate,
} from "./historic-events-for-date";
import {
  HistoricPatientEvent,
  isConsultation,
  isInvestigationActivity,
  isRxInvestigationActivity,
  isRxPrescription,
} from "./types";

const EmptyStateImage = styled("img")({
  margin: "20px auto",
  width: "100px",
});

const PAGE_SIZE = 10;

const UNKNOWN_DATE_KEY = "Unknown date";

const isValidDate = (date: Date) => !Number.isNaN(date.getTime());

const groupEventsByDate = (
  acc: GroupedHistoricEvents,
  historicEvent: HistoricPatientEvent,
): GroupedHistoricEvents => {
  const created = historicEvent.meta?.created;
  let formattedDate: string;
  if (!created) {
    formattedDate = UNKNOWN_DATE_KEY;
  } else {
    const dateObj = new Date(created);
    formattedDate = isValidDate(dateObj)
      ? format(dateObj, DATE_FORMAT)
      : UNKNOWN_DATE_KEY;
  }
  if (!Array.isArray(acc[formattedDate])) {
    acc[formattedDate] = [];
  }
  acc[formattedDate].push(historicEvent);
  return acc;
};

export const sortGroupedHistoricEvents = (
  groupedCardEntities: GroupedHistoricEvents,
): Array<string> => {
  return Object.keys(groupedCardEntities).sort((a, b) => {
    if (a === UNKNOWN_DATE_KEY) return 1;
    if (b === UNKNOWN_DATE_KEY) return -1;
    const dateA = new Date(groupedCardEntities[a][0].meta?.created);
    const dateB = new Date(groupedCardEntities[b][0].meta?.created);
    return dateB.getTime() - dateA.getTime();
  });
};

const DATE_FORMAT = "dd MMM yyyy";

type Props = Omit<React.ComponentProps<typeof HistoricEventsForDate>, "date">;

export const HistoricEventTimeline: FC<Props> = ({
  historicEvents,
  ...restProps
}) => {
  const [diabetesHistoryFlag] = useFeatureFlag(
    featureFlags.diabetesHistoryView,
  );
  const [showDiabetesView, setShowDiabetesView] = useState(false);

  const diabetesConsultationIds = useMemo(
    () =>
      !diabetesHistoryFlag
        ? []
        : historicEvents
            .filter(isConsultation)
            .filter((consultation) =>
              consultation.diseases.some((disease) =>
                disease.displayName.toLowerCase().includes("diabetes"),
              ),
            )
            .map((consultation) => consultation.uuid),
    [historicEvents, diabetesHistoryFlag],
  );
  const diabetesCareEvents = useMemo(
    () =>
      !diabetesHistoryFlag
        ? []
        : historicEvents
            .filter(
              (event) =>
                (isConsultation(event) &&
                  diabetesConsultationIds.includes(event.uuid)) ||
                (isInvestigationActivity(event) &&
                  diabetesConsultationIds.includes(event.clinicalEvent?.id)) ||
                (isRxInvestigationActivity(event) &&
                  diabetesConsultationIds.includes(event.clinicalEvent?.id)) ||
                (isRxPrescription(event) &&
                  diabetesConsultationIds.includes(event.clinicalEvent?.id)),
            )
            .filter((event) => event),
    [historicEvents, diabetesConsultationIds, diabetesHistoryFlag],
  );

  const eventsToShow = showDiabetesView ? diabetesCareEvents : historicEvents;

  if (historicEvents.length < 1) {
    return (
      <Box textAlign="center" py={5}>
        <EmptyStateImage src={emptyStateImage} alt="" />
        <Typography
          variant="body1"
          color="textSecondary"
          style={{ fontWeight: 500 }}
        >
          <FormattedMessage
            id="patient.history.consultationEmpty"
            defaultMessage="No history yet"
          />
        </Typography>
        <Typography variant="body2" color="textSecondary">
          <FormattedMessage
            id="patient.history.consultationEmptyDescription"
            defaultMessage="Consultations and clinical events for this patient will appear here"
          />
        </Typography>
      </Box>
    );
  }

  const showPagination = historicEvents.length > PAGE_SIZE;
  const Timeline = showPagination ? PaginatedTimeline : UnpaginatedTimeline;
  return (
    <>
      {diabetesConsultationIds.length > 0 ? (
        <Button
          fullWidth={false}
          style={{ width: "362px" }}
          color="brand"
          onClick={() => setShowDiabetesView((s) => !s)}
        >
          {showDiabetesView ? "Show full history" : "Show diabetes history"}
        </Button>
      ) : null}
      <Timeline historicEvents={eventsToShow} {...restProps} />
    </>
  );
};

const UnpaginatedTimeline: FC<Props> = ({ historicEvents, ...restProps }) => {
  const eventsGroupedByDate = historicEvents.reduce(groupEventsByDate, {});

  const eventsGroupedByDateSortedByDate =
    sortGroupedHistoricEvents(eventsGroupedByDate);

  return (
    <>
      {eventsGroupedByDateSortedByDate.map((dateStr) => (
        <HistoricEventsForDate
          key={dateStr}
          date={dateStr}
          historicEvents={eventsGroupedByDate[dateStr]}
          {...restProps}
        />
      ))}
    </>
  );
};

const Pagination = styled(_Pagination)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const PaginatedTimeline: FC<Props> = ({ historicEvents, ...restProps }) => {
  const [page, setPage] = useState(1);
  const [paginatedHistoricEvents, setPaginatedHistoricEvents] = useState([]);

  useEffect(() => {
    const totalPages = Math.ceil(historicEvents.length / PAGE_SIZE);
    const currentPage = page > totalPages ? 1 : page; // Reset to page 1 if current page is beyond available pages

    if (currentPage !== page) {
      setPage(currentPage);
    }

    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const pageOfEvents = historicEvents.slice(
      startIndex,
      startIndex + PAGE_SIZE,
    );
    setPaginatedHistoricEvents(pageOfEvents);
  }, [historicEvents, page]);

  const handlePageChange = (_, value: number) => {
    const pageOfEvents = historicEvents.slice(
      (value - 1) * PAGE_SIZE,
      value * PAGE_SIZE,
    );
    setPaginatedHistoricEvents(pageOfEvents);
    setPage(value);
    window.scrollTo(0, 0);
  };

  const eventsGroupedByDate = paginatedHistoricEvents.reduce(
    groupEventsByDate,
    {},
  );

  const eventsGroupedByDateSortedByDate =
    sortGroupedHistoricEvents(eventsGroupedByDate);

  return (
    <>
      {eventsGroupedByDateSortedByDate.map((dateStr) => (
        <HistoricEventsForDate
          key={dateStr}
          date={dateStr}
          historicEvents={eventsGroupedByDate[dateStr]}
          {...restProps}
        />
      ))}
      <Pagination
        count={Math.ceil(historicEvents.length / PAGE_SIZE)}
        page={page}
        onChange={handlePageChange}
      />
    </>
  );
};
